"use client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { Checkbox } from "@/src/components/ui/checkbox";
import { toast } from "@/src/hooks/use-toast";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useRouter } from "@/src/i18n/routing";
import { signIn } from "next-auth/react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useTranslations } from "next-intl";
import { useState } from "react";

// Esquema de validación - se crea dentro del componente para usar traducciones
const createFormSchema = (t: (key: string) => string) =>
  z.object({
    userIdentifier: z.string(),
    password: z.string().min(8, t("passwordMinError")),
    rememberMe: z.boolean().default(false),
  });
interface LoginProps {
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
}
const Login = ({ isLoading, setIsLoading }: LoginProps) => {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const t = useTranslations("Login");
  const auth = useTranslations("AuthNotify");

  const formSchema = createFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      userIdentifier: "",
      password: "",
      rememberMe: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const authResp = await signIn("credentials", {
      userId: values.userIdentifier,
      password: values.password,
      rememberMe: values.rememberMe,
      redirect: false,
    });

    if (authResp?.error) {
      toast({
        title: auth("title"),
        description: auth("credentialsInvalid"),
        variant: "error",
      });
    } else if (authResp?.ok) {
      toast({
        title: auth("title"),
        description:
          auth("welcome") +
          ": " +
          values.userIdentifier.split("@")[0].toLowerCase(),
        variant: "success",
      });
    }

    setIsLoading(false);
  }
  return (
    <div className="w-full max-w-md space-y-8 p-8 bg-card rounded-lg shadow-lg">
      {/* <h1>{t("title")}</h1> */}
      <div className="flex items-center justify-center">
        <Avatar className="w-28 h-10 rounded-lg">
          <AvatarImage
            src={"/img/logoTek_top2.png"}
            alt="Logo Cloud Tek"
          ></AvatarImage>
          <AvatarFallback
            title="Logo"
            className="bg-accent text-accent-foreground"
          />
        </Avatar>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="userIdentifier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("email")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                    <Input
                      placeholder={t("emailPlaceholder")}
                      className="pl-10"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("password")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("remberMe")}
                </FormLabel>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            variant={isLoading ? "default" : "destructive"}
          >
            {isLoading ? t("submitLoading") : t("submit")}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default Login;
